import React from 'react';
import { FaGithub, FaChartLine, FaShieldAlt, FaServer } from 'react-icons/fa';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import projects from '../projects.json';

const articles = [];

const timelineItems = [
  ...projects.map(p => ({ ...p, itemType: 'project' })),
  ...articles.map(a => ({ ...a, itemType: 'article' })),
].sort((a, b) => b.sortDate.localeCompare(a.sortDate));

const StyledLink = styled(Link)`
  margin: 0 20px;
  font-size: 20px;
  color: white;
  text-decoration: none;
  transition: transform 0.3s;
  position: fixed;
  display: flex;
  top: 50%;
  left: 0;
  transform: translateY(0);
`;

const PageContainer = styled.div`
  background-color: #000;
  color: #fff;
  padding: 2rem 2rem 5rem;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h1`
  margin-bottom: 2.5rem;
  font-size: 2.5em;
  color: #fff;
`;

const TimelineWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 700px;
  display: flex;
  flex-direction: column;

  &::before {
    content: '';
    position: absolute;
    left: 6px;
    top: 10px;
    bottom: 10px;
    width: 1px;
    background: #333;
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 10px;
    width: 13px;
    height: 13px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid #fff;
    z-index: 1;
  }
`;

const TimelineRow = styled.div`
  display: flex;
  gap: 22px;
  padding-bottom: 28px;
  position: relative;
`;

const Dot = styled.div`
  flex-shrink: 0;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  margin-top: 5px;
  z-index: 1;
  background: ${props => props.itemType === 'article' ? '#000' : '#fff'};
  border: 2px solid ${props => props.itemType === 'article' ? '#555' : '#fff'};
`;

const Card = styled.div`
  flex: 1;
  background-color: #222;
  border-radius: 10px;
  box-shadow: 0 10px 30px -15px rgba(255, 255, 255, 0.3);
  padding: 18px 20px;
  transition: transform 0.3s;
  &:hover {
    transform: translateY(-4px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

const TypeBadge = styled.span`
  font-size: 0.68em;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${props => props.itemType === 'article' ? '#888' : '#aaa'};
`;

const DateLabel = styled.span`
  font-size: 0.75em;
  color: #555;
`;

const seriesColors = {
  Trading:   '#7a9cc7',
  'AI Safety': '#c79a7a',
  MCP:       '#7ac79a',
};

const SeriesTag = styled.span`
  font-size: 0.68em;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: ${props => seriesColors[props.series] || '#888'};
`;

const StatusTag2 = styled.span`
  font-size: 0.68em;
  letter-spacing: 0.03em;
  color: ${props =>
    props.status === 'live' || props.status === 'posted' ? '#9fdf9f' :
    props.status === 'ongoing' ? '#6a9a7a' :
    '#555'
  };
`;

const CardTitle = styled.h2`
  margin: 0 0 10px 0;
  font-size: 1.1em;
  font-weight: 500;
  color: #fff;

  .project-icon {
    vertical-align: text-bottom;
    margin-right: 6px;
  }
`;

const TagBadge = styled.span`
  display: inline-block;
  background: #2a2a2a;
  color: #aaa;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.7em;
  font-weight: 600;
  margin-left: 8px;
  vertical-align: middle;
  letter-spacing: 0.04em;
`;

const TechRow = styled.div`
  margin: 8px 0 10px;
`;

const Skill = styled.span`
  display: inline-block;
  background: #333;
  color: #fff;
  border-radius: 4px;
  padding: 3px 8px;
  margin-right: 6px;
  margin-bottom: 6px;
  font-size: 0.82em;
`;

const Description = styled.p`
  color: #bbb;
  line-height: 1.6;
  margin: 0 0 10px;
  font-size: 0.93em;
`;

const RepoLink = styled.a`
  color: #fff;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  font-size: 0.9em;
  svg {
    margin-right: 6px;
  }
  &:hover {
    color: #ccc;
  }
`;

const PlatformTag = styled.span`
  display: inline-block;
  background: #333;
  color: #ccc;
  border-radius: 4px;
  padding: 3px 9px;
  font-size: 0.75em;
  font-weight: 600;
  letter-spacing: 0.03em;
`;


const getIcon = (iconName) => {
  switch (iconName) {
    case "FaChartLine": return <FaChartLine className="project-icon" size="1.2em" />;
    case "FaShieldAlt": return <FaShieldAlt className="project-icon" size="1.2em" />;
    case "FaServer":    return <FaServer className="project-icon" size="1.2em" />;
    default: return null;
  }
};

function Projects() {
  return (
    <PageContainer>
      <Title>Projects</Title>
      <TimelineWrapper>
        {timelineItems.map((item, index) => (
          <TimelineRow key={index}>
            <Dot itemType={item.itemType} />
            <Card>
              <CardHeader>
                <TypeBadge itemType={item.itemType}>{item.itemType}</TypeBadge>
                {item.date && item.date !== 'Coming Soon' && <DateLabel>· {item.date}</DateLabel>}
                {item.series && <SeriesTag series={item.series}>· #{item.series}</SeriesTag>}
                {item.status && <StatusTag2 status={item.status}>· {item.status}</StatusTag2>}
              </CardHeader>
              <CardTitle>
                {item.itemType === 'project' && getIcon(item.icon)}
                {item.itemType === 'article' && item.link ? (
                  <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'none' }}>
                    {item.title}
                  </a>
                ) : (
                  item.title
                )}
                {item.tag && <TagBadge>{item.tag}</TagBadge>}
              </CardTitle>
              {item.itemType === 'project' && (
                <>
                  <TechRow>
                    {item.tech.map((skill, i) => <Skill key={i}>{skill}</Skill>)}
                  </TechRow>
                  <Description>{item.description}</Description>
                  <RepoLink href={item.github} target="_blank" rel="noopener noreferrer">
                    <FaGithub size="1.2em" />
                    GitHub
                  </RepoLink>
                </>
              )}
              {item.itemType === 'article' && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <PlatformTag>{item.platform}</PlatformTag>
                </div>
              )}
            </Card>
          </TimelineRow>
        ))}
      </TimelineWrapper>
      <StyledLink to="/">← Back</StyledLink>
    </PageContainer>
  );
}

export default Projects;
